import { addDays, differenceInCalendarDays, formatISO, parseISO } from 'date-fns';
import type { PlanRow } from 'modules/campaign/row';
import type { PlanMeta } from '../state';
import type { PlanResponse } from '../types';

const NO_SCHEDULE_REASON = 'Source plan has no schedule to import.';
const EXACT_OUTSIDE_REASON = 'Source schedule falls outside the destination period.';
const ALIGN_OUTSIDE_REASON = 'Aligned schedule would end after the destination period.';

export type ScheduleAnalysis = {
  rowCount: number;
  first: string | null;
  last: string | null;
  alignedFirst: string | null;
  alignedLast: string | null;
  exactEnabled: boolean;
  alignEnabled: boolean;
  exactDisabledReason?: string;
  alignDisabledReason?: string;
};

type SerializedScheduleRow = PlanRow & {
  positions?: { date_from: string; date_to: string }[];
  radio_placements?: { date: string }[];
};

function includeFirst(value: string, bounds: { first: string | null }): void {
  if (bounds.first === null || value < bounds.first) bounds.first = value;
}

function includeLast(value: string, bounds: { last: string | null }): void {
  if (bounds.last === null || value > bounds.last) bounds.last = value;
}

function destinationScheduleField(row: PlanRow, destination: PlanMeta): 'positions' | 'radio_placements' | null {
  if (row.kind === 'SMART') return null;
  if (destination.plan_strategy === 'RADIO' && row.kind === 'MEDIA') return 'radio_placements';
  return 'positions';
}

export function analyzeSchedule(source: PlanResponse, destination: PlanMeta): ScheduleAnalysis {
  const bounds: { first: string | null; last: string | null } = { first: null, last: null };

  for (const planRow of source.rows) {
    const row = planRow as SerializedScheduleRow;
    const field = destinationScheduleField(planRow, destination);

    if (field === 'positions' && Array.isArray(row.positions)) {
      for (const position of row.positions) {
        includeFirst(position.date_from, bounds);
        includeLast(position.date_to, bounds);
      }
    } else if (field === 'radio_placements' && Array.isArray(row.radio_placements)) {
      for (const placement of row.radio_placements) {
        includeFirst(placement.date, bounds);
        includeLast(placement.date, bounds);
      }
    }
  }

  if (bounds.first === null || bounds.last === null) {
    return {
      rowCount: source.rows.length,
      first: null,
      last: null,
      alignedFirst: null,
      alignedLast: null,
      exactEnabled: false,
      alignEnabled: false,
      exactDisabledReason: NO_SCHEDULE_REASON,
      alignDisabledReason: NO_SCHEDULE_REASON,
    };
  }

  const firstDate = parseISO(bounds.first);
  const destinationStart = parseISO(destination.date_from);
  const weekdayDelta = (firstDate.getDay() - destinationStart.getDay() + 7) % 7;
  const alignedFirstDate = addDays(destinationStart, weekdayDelta);
  const offset = differenceInCalendarDays(alignedFirstDate, firstDate);
  const alignedFirst = formatISO(alignedFirstDate, { representation: 'date' });
  const alignedLast = formatISO(addDays(parseISO(bounds.last), offset), { representation: 'date' });
  const exactEnabled = bounds.first >= destination.date_from && bounds.last <= destination.date_to;
  const alignEnabled = alignedLast <= destination.date_to;

  return {
    rowCount: source.rows.length,
    first: bounds.first,
    last: bounds.last,
    alignedFirst,
    alignedLast,
    exactEnabled,
    alignEnabled,
    exactDisabledReason: exactEnabled ? undefined : EXACT_OUTSIDE_REASON,
    alignDisabledReason: alignEnabled ? undefined : ALIGN_OUTSIDE_REASON,
  };
}
